// Radha — reporting-currency control.
//
// Two ideas in one small select. "Auto" makes money follow the country lens,
// so Germany reads in EUR and Saudi in SAR — which is what a local finance
// team expects. Picking a currency instead pins EVERY figure to it, which is
// what a group CFO does to read subsidiaries side by side.
//
// Translation is presentation only: the book, the stored amounts and the
// Excel exports keep their own units. Rates are the USD-anchored quotes from
// fx_rates (070), so a cross rate is one division and a missing pair leaves
// the figure in its own currency rather than inventing a number.
import React from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import {
  COUNTRIES, countryDef, setReportingCurrency, getReportingOverride,
  useReportingCurrency, useViewCountry,
} from "../lib/jurisdiction";

const OPTIONS = ["INR", "USD", "EUR", "GBP", "AED", "SAR"];

/** Latest USD-anchored quote per currency: units of X per 1 USD. */
export function useFxRateMap() {
  return useQuery({
    queryKey: ["fx-rate-map"],
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase
        .from("fx_rates")
        .select("currency, units_per_anchor, quote_date")
        .order("quote_date", { ascending: false });
      if (error) throw error;
      const out: Record<string, number> = {};
      for (const r of data ?? []) {
        // first row per currency wins — the query is newest-first
        if (out[r.currency] === undefined) out[r.currency] = Number(r.units_per_anchor);
      }
      return out;
    },
    staleTime: 600_000,
  });
}

export function CurrencySwitcher({ tenantCountry }: { tenantCountry: string | null | undefined }) {
  const { currency, forced } = useReportingCurrency(tenantCountry);
  const view = useViewCountry(tenantCountry);
  const auto = countryDef(view)?.currency ?? "INR";

  return (
    <select
      value={getReportingOverride() ?? "auto"}
      onChange={(e) => setReportingCurrency(e.target.value === "auto" ? null : e.target.value)}
      title="Reporting currency. Auto follows the country lens; picking one converts every figure into it for display, at the latest quoted rate. Stored amounts and exports are unchanged."
      aria-label="Reporting currency"
      className="sanctuary-btn"
      style={{
        height: 30, padding: "0 8px", fontSize: 12.5, borderRadius: 9, cursor: "pointer",
        border: forced ? "1px solid var(--accent-purple)" : "1px solid var(--border)",
        background: "var(--surface)", color: forced ? "var(--accent-purple)" : "var(--fg-muted)",
      }}
    >
      <option value="auto">Auto ({auto})</option>
      {OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );
}

/** Shown in the jurisdiction ribbon when figures are being translated. */
export function TranslationNote({ tenantCountry }: { tenantCountry: string | null | undefined }) {
  const { currency, forced } = useReportingCurrency(tenantCountry);
  const view = useViewCountry(tenantCountry);
  const home = COUNTRIES.find((c) => c.code === (tenantCountry ?? "IN"));
  const homeCur = home?.currency ?? "INR";
  if (!forced && currency === homeCur) return null;
  return (
    <span style={{ fontSize: 11.5, color: "var(--accent-purple)" }}>
      figures translated {homeCur} → {currency} at the latest quoted rate (display only)
    </span>
  );
}
